import { Injectable, inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { CatalogRepository } from '@service/catalog/catalog.repository';
import {
  Priority,
  SpecimenType,
  WellPlateConfig,
  ExtractionMethod,
  ExtractionMethodType,
  Cell,
  Plate,
  Index,
  IndexAdapter,
  Configuration,
  ConfigurationParameterType,
  FailureCause,
  FailureContextType,
  ReprocessStatus
} from '@model/catalog/catalog.model';
import { environment } from '@env/environment';
import {
  CellResponse,
  ConfigurationResponse,
  ExamGroup,
  ExamGroupResponse,
  ExtractionMethodResponse,
  FailureCauseResponse,
  IndexAdapterResponse,
  IndexResponse,
  PlateResponse,
  PriorityResponse,
  ReprocessStatusResponse,
  SpecimenTypeResponse,
  WellPlateConfigResponse
} from './catalog-backend.dto';

/**
 * HTTP Repository Implementation for Catalog operations
 * Connects to backend endpoints:
 * - GET /catalogs/priorities
 * - GET /catalogs/specimen-types
 * - GET /catalogs/exam-groups
 * - GET /catalogs/well-plates
 * - GET /catalogs/extraction-methods
 * - GET /catalogs/cells
 * - GET /catalogs/plates
 * - GET /catalogs/indexes
 * - GET /catalogs/index-adapters
 */
@Injectable({
  providedIn: 'root'
})
export class HttpCatalogRepository extends CatalogRepository {
  private readonly http = inject(HttpClient);
  private readonly BASE_URL = `${environment.laboratory.baseUrl}${environment.laboratory.context}/catalogs`;

  override getAllPriorities(): Observable<Priority[]> {
    return this.http
      .get<PriorityResponse[]>(`${this.BASE_URL}/priorities`)
      .pipe(
        map((response) =>
          response.map((item) => ({
            id: item.id,
            name: item.name
          }))
        ),
        catchError((error: HttpErrorResponse) => {
          // Error handling delegated to interceptor - just propagate
          return throwError(() => error);
        })
      );
  }

  override getAllSpecimenTypes(): Observable<SpecimenType[]> {
    return this.http
      .get<SpecimenTypeResponse[]>(`${this.BASE_URL}/specimen-types`)
      .pipe(
        map((response) =>
          response.map((item) => ({
            id: item.id,
            name: item.name,
            specimenCode: item.specimenCode
          }))
        ),
        catchError((error: HttpErrorResponse) => {
          // Error handling delegated to interceptor - just propagate
          return throwError(() => error);
        })
      );
  }

  override getAllExamGroups(): Observable<ExamGroup[]> {
    return this.http
      .get<ExamGroupResponse[]>(`${this.BASE_URL}/exam-groups`)
      .pipe(
        map((response) =>
          response.map((item) => ({
            id: item.id,
            name: item.name
          }))
        ),
        catchError((error: HttpErrorResponse) => {
          // Error handling delegated to interceptor - just propagate
          return throwError(() => error);
        })
      );
  }

  override getAllCells(): Observable<Cell[]> {
    return this.http.get<CellResponse[]>(`${this.BASE_URL}/cells`).pipe(
      map((response) =>
        response.map((item) => ({
          id: item.id,
          name: item.name,
          status: item.status,
          createdAt: item.createdAt
        }))
      ),
      catchError((error: HttpErrorResponse) => {
        // Error handling delegated to interceptor - just propagate
        return throwError(() => error);
      })
    );
  }

  override getAllPlates(): Observable<Plate[]> {
    return this.http.get<PlateResponse[]>(`${this.BASE_URL}/plates`).pipe(
      map((response) =>
        response.map((item) => ({
          id: item.id,
          cellId: item.cellId,
          name: item.name,
          status: item.status,
          createdAt: item.createdAt
        }))
      ),
      catchError((error: HttpErrorResponse) => {
        // Error handling delegated to interceptor - just propagate
        return throwError(() => error);
      })
    );
  }

  override getAllIndexes(): Observable<Index[]> {
    return this.http.get<IndexResponse[]>(`${this.BASE_URL}/indexes`).pipe(
      map((response) =>
        response.map((item) => ({
          id: item.id,
          plateId: item.plateId,
          indexGroup: item.indexGroup,
          status: item.status,
          createdAt: item.createdAt
        }))
      ),
      catchError((error: HttpErrorResponse) => {
        // Error handling delegated to interceptor - just propagate
        return throwError(() => error);
      })
    );
  }

  override getIndexesByPlateId(plateId: string): Observable<Index[]> {
    return this.http
      .get<IndexResponse[]>(`${this.BASE_URL}/indexes?plateId=${plateId}`)
      .pipe(
        map((response) =>
          response.map((item) => ({
            id: item.id,
            plateId: item.plateId,
            indexGroup: item.indexGroup,
            status: item.status,
            createdAt: item.createdAt
          }))
        ),
        catchError((error: HttpErrorResponse) => {
          // Error handling delegated to interceptor - just propagate
          return throwError(() => error);
        })
      );
  }

  override getIndexAdapters(
    plateId: string,
    indexId: string
  ): Observable<IndexAdapter[]> {
    const url = `${this.BASE_URL}/index-adapters?plateId=${plateId}&indexId=${indexId}`;
    return this.http.get<IndexAdapterResponse[]>(url).pipe(
      map((response) =>
        response.map((item) => ({
          indexAdapterId: item.indexAdapterId,
          indexId: item.indexId,
          indexName: item.indexName,
          i7Bases: item.i7Bases
        }))
      ),

      catchError((error: HttpErrorResponse) => {
        // Error handling delegated to interceptor - just propagate
        return throwError(() => error);
      })
    );
  }

  override getWellPlateConfigs(): Observable<WellPlateConfig[]> {
    return this.http
      .get<WellPlateConfigResponse[]>(`${this.BASE_URL}/well-plates`)
      .pipe(
        map((configs) =>
          configs.map((c) => ({
            id: c.id,
            rowLetters: c.rowLetters,
            columnCount: c.columnCount,
            totalWells: c.totalWells,
            wells: c.wells
          }))
        ),
        catchError((error) => {
          console.error('Error loading well plate configurations:', error);
          return throwError(() => error);
        })
      );
  }

  override getExtractionMethods(): Observable<ExtractionMethod[]> {
    return this.http
      .get<ExtractionMethodResponse[]>(`${this.BASE_URL}/extraction-methods`)
      .pipe(
        map((methods) =>
          methods.map((m) => ({
            id: m.id,
            name: m.name,
            type: this.mapMethodNameToType(m.name)
          }))
        ),
        catchError((error) => {
          console.error('Error loading extraction methods:', error);
          return throwError(() => error);
        })
      );
  }

  private mapMethodNameToType(methodName: string): ExtractionMethodType {
    if (methodName.toUpperCase().includes('SEMI')) {
      return 'SEMI_AUTOMATIC';
    }
    return 'MANUAL';
  }

  override getConfigurationsByType(
    type: ConfigurationParameterType
  ): Observable<Configuration[]> {
    return this.http
      .get<
        ConfigurationResponse[]
      >(`${this.BASE_URL}/configurations?type=${type}`)
      .pipe(
        map((response) =>
          response.map((item) => ({
            id: item.id,
            name: item.name,
            value: item.value,
            type: item.type as ConfigurationParameterType
          }))
        ),
        catchError((error: HttpErrorResponse) => {
          // Error handling delegated to interceptor - just propagate
          return throwError(() => error);
        })
      );
  }

  /**
   * Get failure causes for sequencing runs.
   * Endpoint: GET /catalogs/causes/runs
   * Used in HU43: Marcar corrida como no exitosa.
   * @param parameterType Optional technology filter (NGS, MICROARREGLOS, MLPA, EXPANSION_TRIPLETAS)
   */
  override getRunFailureCauses(parameterType?: string): Observable<FailureCause[]> {
    const options: { params?: Record<string, string> } = {};
    if (parameterType) {
      options.params = { parameterType: parameterType.toUpperCase() };
    }

    return this.http
      .get<FailureCauseResponse[]>(`${this.BASE_URL}/causes/runs`, options)
      .pipe(
        map((response) =>
          response.map((item) => ({
            id: item.causesId,
            description: item.causeDescription,
            context: 'RUN' as FailureContextType
          }))
        ),
        catchError((error: HttpErrorResponse) => {
          // Error handling delegated to interceptor - just propagate
          return throwError(() => error);
        })
      );
  }

  /**
   * Get failure causes for run success reprocess.
   * Endpoint: GET /catalogs/causes/run-success
   * Used in HU44/HU45: Marcar corrida como exitosa / Reprocesos.
   * @param parameterType Optional technology filter (NGS, MICROARREGLOS, MLPA, EXPANSION_TRIPLETAS)
   */
  override getRunSuccessCauses(
    parameterType?: string
  ): Observable<FailureCause[]> {
    const options: { params?: Record<string, string> } = {};
    if (parameterType) {
      options.params = { parameterType: parameterType.toUpperCase() };
    }

    return this.http
      .get<
        FailureCauseResponse[]
      >(`${this.BASE_URL}/causes/run-success`, options)
      .pipe(
        map((response) =>
          response.map((item) => ({
            id: item.causesId,
            description: item.causeDescription,
            context: 'RUN_SUCCESS' as FailureContextType
          }))
        ),
        catchError((error: HttpErrorResponse) => {
          // Error handling delegated to interceptor - just propagate
          return throwError(() => error);
        })
      );
  }

  /**
   * Get failure causes for sequencing specimen reprocess.
   * Endpoint: GET /catalogs/causes/sequencing
   */
  override getSequencingReprocessCauses(): Observable<FailureCause[]> {
    return this.http
      .get<FailureCauseResponse[]>(`${this.BASE_URL}/causes/sequencing`)
      .pipe(
        map((response) =>
          response.map((item) => ({
            id: item.causesId,
            description: item.causeDescription,
            context: 'SEQUENCING' as FailureContextType
          }))
        ),
        catchError((error: HttpErrorResponse) => {
          // Error handling delegated to interceptor - just propagate
          return throwError(() => error);
        })
      );
  }

  /**
   * Get all failure causes without context filter.
   * Endpoint: GET /catalogs/causes
   * Used in Observations filter to show all available causes.
   */
  override getAllCauses(): Observable<FailureCause[]> {
    return this.http
      .get<FailureCauseResponse[]>(`${this.BASE_URL}/causes`)
      .pipe(
        map((response) =>
          response.map((item) => ({
            id: item.causesId,
            description: item.causeDescription,
            context: 'ALL' as FailureContextType
          }))
        ),
        catchError((error: HttpErrorResponse) => {
          // Error handling delegated to interceptor - just propagate
          return throwError(() => error);
        })
      );
  }

  override getReprocessStatuses(): Observable<ReprocessStatus[]> {
    return this.http
      .get<ReprocessStatusResponse[]>(`${this.BASE_URL}/reprocess-statuses`)
      .pipe(
        map((response) =>
          response.map((item) => ({
            id: item.id,
            description: item.description,
            order: item.order
          }))
        ),
        catchError((error: HttpErrorResponse) => {
          // Error handling delegated to interceptor - just propagate
          return throwError(() => error);
        })
      );
  }
}
